// Shared interface behaviour. Everything is progressive enhancement on top
// of working Liquid markup: links still navigate and forms still post
// without this module.
//
// - One overlay manager for the menu, mini-cart, quick drawer and checkout
//   step: native <dialog> (modal: the page behind is inert), Escape,
//   backdrop click, scroll lock, focus into the overlay and focus back to
//   the opener. Only one overlay is open at a time; opening another swaps
//   the layers instead of stacking them.
// - Quantity steppers, the product gallery, the sticky buy bar, the phone
//   order of the pack cards and their chip emphasis initialise per element and are
//   idempotent; section load/unload in the Theme Editor re-runs init and
//   disconnects observers (no body-wide DOM observer).

let current = null;

export function openOverlay(dialog, opener = null) {
  if (!dialog) return;
  if (current && current.dialog !== dialog) closeOverlay(current.dialog, { restore: false });
  current = { dialog, opener: opener || document.activeElement };
  if (!dialog.open) dialog.showModal();
  document.documentElement.classList.add('is-locked');
  for (const t of document.querySelectorAll(`[aria-controls="${dialog.id}"]`)) t.setAttribute('aria-expanded', 'true');
  const target = dialog.querySelector('[data-autofocus]') || dialog.querySelector('[data-overlay-close]');
  if (target) target.focus();
}

export function closeOverlay(dialog = current && current.dialog, { restore = true } = {}) {
  if (!dialog) return;
  const opener = current && current.dialog === dialog ? current.opener : null;
  if (dialog.open) dialog.close();
  if (current && current.dialog === dialog) current = null;
  if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
  for (const t of document.querySelectorAll(`[aria-controls="${dialog.id}"]`)) t.setAttribute('aria-expanded', 'false');
  if (restore && opener && opener.isConnected) opener.focus();
}

export function currentOverlay() {
  return current;
}

/**
 * V07 (r149): the submit buttons of one product form as the DOM holds them
 * NOW. A section re-render can replace the form and its button while a
 * request runs, so the buttons are looked up again (by form id, also the
 * buttons associated with form="…", e.g. the phone buy bar).
 */
export function addButtonsFor(form, doc = document) {
  const id = form && form.id;
  const live = (id && doc.getElementById(id)) || form;
  const out = [];
  if (id) for (const b of doc.querySelectorAll(`[type="submit"][form="${id}"]`)) out.push(b);
  if (live) for (const b of live.querySelectorAll('[type="submit"]')) if (!out.includes(b)) out.push(b);
  return out;
}

/** The inline loader is aria-busy on every current button of the form (CSS draws the spinner). */
export function setAddBusy(form, on, doc = document) {
  for (const b of addButtonsFor(form, doc)) {
    if (on) b.setAttribute('aria-busy', 'true');
    else b.removeAttribute('aria-busy');
  }
}

/**
 * One request per accepted add: a guard per form id, so a re-rendered form
 * (same id, fresh button) stays guarded until its request has finished.
 */
export function createAddGuard(doc = document) {
  const busy = new Set();
  const key = (form) => form.id || form;
  return {
    begin(form) {
      if (busy.has(key(form))) return false;
      busy.add(key(form));
      setAddBusy(form, true, doc);
      return true;
    },
    end(form) {
      busy.delete(key(form));
      setAddBusy(form, false, doc);
    },
    has: (form) => busy.has(key(form))
  };
}

function bindDialog(dialog) {
  if (dialog.dataset.ppBound) return;
  dialog.dataset.ppBound = '1';
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    if (dialog.dataset.overlayLocked) return;
    dialog.dispatchEvent(new CustomEvent('pp:overlay-cancel', { bubbles: true }));
    if (!dialog.hasAttribute('data-overlay-managed')) closeOverlay(dialog);
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) {
      dialog.dispatchEvent(new CustomEvent('pp:overlay-cancel', { bubbles: true }));
      if (!dialog.hasAttribute('data-overlay-managed')) closeOverlay(dialog);
    }
  });
  // closing for any other reason (form method=dialog, script) keeps state right
  dialog.addEventListener('close', () => {
    if (current && current.dialog === dialog) current = null;
    if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('is-locked');
  });
}

/* ------------------------------------------------------------ steppers */

function stepQuantity(button) {
  const group = button.closest('[data-qty]');
  const input = group && group.querySelector('input');
  if (!input || input.disabled) return;
  const step = Number(input.step) || 1;
  const min = input.min === '' ? 0 : Number(input.min);
  const max = input.max === '' ? Infinity : Number(input.max);
  const now = Number(input.value) || 0;
  const next = Math.min(max, Math.max(min, now + Number(button.dataset.qtyStep) * step));
  if (next === now) return;
  input.value = String(next);
  syncStepper(group);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

export function syncStepper(group) {
  const input = group.querySelector('input');
  if (!input) return;
  const v = Number(input.value) || 0;
  const min = input.min === '' ? 0 : Number(input.min);
  const max = input.max === '' ? Infinity : Number(input.max);
  const minus = group.querySelector('[data-qty-step="-1"]');
  const plus = group.querySelector('[data-qty-step="1"]');
  if (minus) minus.disabled = v <= min;
  if (plus) plus.disabled = v >= max;
}

/* ------------------------------------------------------- per-element init */

const disposers = new WeakMap();

function onDispose(section, fn) {
  if (!section) return;
  const list = disposers.get(section) || [];
  list.push(fn);
  disposers.set(section, list);
}

export function dispose(section) {
  for (const fn of disposers.get(section) || []) {
    try { fn(); } catch { /* keep going */ }
  }
  disposers.delete(section);
}

function initGallery(root) {
  const track = root.querySelector('[data-gallery-track]');
  const thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
  const dots = [...root.querySelectorAll('[data-gallery-dot]')];
  if (!track) return;
  const slides = [...track.children];
  let current = 0;
  const mark = (i) => {
    current = i;
    thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
    dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));
  };
  const go = (i, focus) => {
    const slide = slides[i];
    if (!slide) return;
    // the phone track has an inline padding (the peek); the slide starts after it
    const pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft - pad, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    mark(i);
    if (focus) slide.focus({ preventScroll: true });
  };
  const onClick = (e) => {
    const t = e.target.closest('[data-gallery-thumb]');
    if (t) go(Number(t.dataset.galleryThumb), false);
  };
  // arrow keys on the focusable track step through the photos (HTML gallery)
  const onKey = (e) => {
    if (e.target !== track || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    go(Math.min(slides.length - 1, Math.max(0, current + (e.key === 'ArrowRight' ? 1 : -1))), false);
  };
  root.addEventListener('click', onClick);
  track.addEventListener('keydown', onKey);
  let raf = 0;
  // the scroll position is the source of truth: the slide nearest the middle
  const onScroll = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const mid = track.scrollLeft + track.clientWidth / 2;
      let best = 0;
      let bestD = Infinity;
      slides.forEach((s, i) => {
        const d = Math.abs(s.offsetLeft - track.offsetLeft + s.offsetWidth / 2 - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      mark(best);
    });
  };
  track.addEventListener('scroll', onScroll, { passive: true });
  onDispose(root.closest('.shopify-section'), () => {
    root.removeEventListener('click', onClick);
    track.removeEventListener('keydown', onKey);
    track.removeEventListener('scroll', onScroll);
    cancelAnimationFrame(raf);
  });
}

function initBuybar(bar) {
  const target = document.getElementById(bar.dataset.buybar);
  if (!target || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(([entry]) => {
    const below = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
    bar.toggleAttribute('data-visible', below);
    bar.setAttribute('aria-hidden', below ? 'false' : 'true');
    bar.inert = !below;
  });
  io.observe(target);
  bar.inert = true;
  onDispose(bar.closest('.shopify-section'), () => io.disconnect());
}

/**
 * pack-calc output 'features' ("handle~count|…"; a handle may repeat and is
 * summed) -> Map handle -> count, or null when there is nothing: an invalid
 * or unknown structure is unknown, never 0.
 */
export function parseFeatures(text) {
  const out = new Map();
  for (const part of String(text || '').split('|')) {
    const [h, n] = part.split('~');
    if (!h || n === undefined || !/^\d+$/.test(n.trim())) continue;
    out.set(h.trim(), (out.get(h.trim()) || 0) + Number(n));
  }
  return out.size ? out : null;
}

/**
 * One chip against the first pack of its showcase (HTML packs.js chips()):
 * 'new' when none of its features is in the base (chip--em and "+ "),
 * 'more' when one is new or counts more (chip--em), otherwise 'none'.
 * Without own or base counts nothing is claimed.
 */
export function chipMark(handles, own, base) {
  if (!own || !base || !handles || !handles.length) return 'none';
  // only features this pack really counts take part (a 0 or absent own count claims nothing)
  const present = handles.filter((h) => (own.get(h) || 0) > 0);
  if (!present.length) return 'none';
  let missing = 0;
  let more = false;
  for (const h of present) {
    const b = base.get(h) || 0;
    if (b === 0) missing += 1;
    else if (own.get(h) > b) more = true;
  }
  if (missing === present.length) return 'new';
  return missing > 0 || more ? 'more' : 'none';
}

/**
 * The marks of one showcase. cards: [{ order, features (string|undefined),
 * chips: [handle[]] }] in any DOM order. Round 514: the base is the entry
 * pack from the data, i.e. the valid card whose every feature count is <= the
 * count of every other valid card (STARTER), so the editor or phone order
 * never moves it; only when no such card exists, the card with the lowest
 * render order that carries feature data (a chosen pack). The base itself is
 * never marked; an invalid base marks nothing.
 */
export function packChipPlan(cards) {
  const chosen = cards.filter((c) => c.features !== undefined).sort((a, b) => a.order - b.order);
  const maps = new Map(chosen.map((c) => [c, parseFeatures(c.features)]));
  const valid = chosen.filter((c) => maps.get(c));
  const entry = valid.find((c) => valid.every((o) => o === c || [...maps.get(c)].every(([h, n]) => n <= (maps.get(o).get(h) || 0))));
  const base = entry || chosen[0] || null;
  const baseMap = base ? maps.get(base) : null;
  return cards.map((c) => (c === base || !baseMap ? c.chips.map(() => 'none') : c.chips.map((h) => chipMark(h, parseFeatures(c.features), baseMap))));
}

/** Apply the plan to one grid (render order from data-wide-index); idempotent. */
export function initPackChips(grid) {
  const top = (el) => { let n = el; while (n && n.parentElement !== grid) n = n.parentElement; return n; };
  const els = [...grid.querySelectorAll('[data-pack-features]')];
  const cards = els.map((el) => {
    const t = top(el);
    const chips = [...el.querySelectorAll('.chip')];
    return { el, chips, order: Number(t && t.dataset.wideIndex !== undefined ? t.dataset.wideIndex : [...grid.children].indexOf(t)), features: el.dataset.packFeatures, handles: chips.map((li) => (li.dataset.chipFeatures || '').split(' ').filter(Boolean)) };
  });
  const plan = packChipPlan(cards.map((c) => ({ order: c.order, features: c.features, chips: c.handles })));
  cards.forEach((c, i) => c.chips.forEach((li, j) => {
    li.classList.remove('chip--em');
    for (const old of li.querySelectorAll('[data-pp-chip-plus]')) old.remove();
    const mark = plan[i][j];
    if (mark === 'none') return;
    li.classList.add('chip--em');
    if (mark === 'new') {
      const plus = document.createElement('span');
      plus.dataset.ppChipPlus = '';
      plus.textContent = '+ ';
      li.prepend(plus);
    }
  }));
}

function initPackOrder(grid) {
  const cards = [...grid.children];
  cards.forEach((c, i) => { if (!c.dataset.wideIndex) c.dataset.wideIndex = String(i); });
  // the chip emphasis compares in render order, before the phone order moves cards
  initPackChips(grid);
  const mq = matchMedia('(max-width: 599px)');
  const apply = () => {
    const key = mq.matches ? (c) => Number(c.dataset.phonePosition || 99) * 100 + Number(c.dataset.wideIndex) : (c) => Number(c.dataset.wideIndex);
    const wanted = [...grid.children].sort((a, b) => key(a) - key(b));
    if (wanted.every((c, i) => grid.children[i] === c)) return;
    const active = grid.contains(document.activeElement) ? document.activeElement : null;
    wanted.forEach((c) => grid.appendChild(c));   // moves, never clones
    if (active) active.focus({ preventScroll: true });
  };
  apply();
  mq.addEventListener('change', apply);
  onDispose(grid.closest('.shopify-section'), () => mq.removeEventListener('change', apply));
}

/** Round 492 (D): a compact pack card's contents disclosure. The markup is open (no JavaScript and wide screens
 *  show everything); on a phone it starts closed, and back on a wide screen it opens again. A visitor's own toggle on
 *  the phone is kept until the viewport changes. */
function initPackMore(details) {
  const mq = matchMedia('(max-width: 599px)');
  const apply = () => { details.open = !mq.matches; };
  apply();
  mq.addEventListener('change', apply);
  onDispose(details.closest('.shopify-section'), () => mq.removeEventListener('change', apply));
}

const initialised = new WeakSet();

/** Initialise everything below root once (root may be a re-rendered section). */
export function init(root = document) {
  for (const d of root.querySelectorAll('dialog')) bindDialog(d);
  for (const g of root.querySelectorAll('[data-qty]')) syncStepper(g);
  const once = (selector, fn) => {
    for (const el of root.querySelectorAll(selector)) {
      if (initialised.has(el)) continue;
      initialised.add(el);
      fn(el);
    }
  };
  once('[data-gallery]', initGallery);
  once('[data-buybar]', initBuybar);
  once('[data-pack-grid]', initPackOrder);
  once('[data-pack-more]', initPackMore);
}

/* --------------------------------------------------------- quick drawer */

let quickAbort = null;

// D176 (round 437): an own product page, canonical or a collection product
// route, with an optional market locale prefix; nothing after the handle
const PRODUCT_PATH = /^(\/[a-z]{2}(?:-[a-z]{2})?)?(?:\/collections\/[^/]+)?\/products\/([a-z0-9][a-z0-9-]*)\/?$/i;

/**
 * The drawer URL of a link inside a [data-quick-links] scope (a product
 * description), or null when the link must simply navigate: another origin,
 * mailto/tel, an anchor, a download, a new tab/window target, a non-product
 * page, or the product of the current page itself. A collection product
 * route becomes the canonical /products/<handle>; only an explicit numeric
 * ?variant= is kept, so the drawer shows that variant's price and state.
 */
export function quickLinkTarget(a, { origin = window.location.origin, currentPath = window.location.pathname } = {}) {
  if (!a || a.hasAttribute('download') || a.hasAttribute('data-quick-view')) return null;
  const target = (a.getAttribute('target') || '').trim().toLowerCase();
  if (target && target !== '_self') return null;
  const raw = (a.getAttribute('href') || '').trim();
  if (!raw || raw.startsWith('#')) return null;
  let u;
  try { u = new URL(raw, `${origin}${currentPath}`); } catch { return null; }
  if (u.origin !== origin) return null;
  const m = PRODUCT_PATH.exec(u.pathname);
  if (!m) return null;
  const here = PRODUCT_PATH.exec(currentPath);
  if (here && here[2].toLowerCase() === m[2].toLowerCase()) return null;
  const variant = u.searchParams.get('variant');
  return `${m[1] || ''}/products/${m[2].toLowerCase()}${variant && /^\d+$/.test(variant) ? `?variant=${variant}` : ''}`;
}

/** A plain primary click: modified, middle, already handled clicks keep the browser's own behaviour. */
const plainClick = (e) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;

async function openQuickView(link, href = null) {
  const dialog = document.getElementById('pp-quick-drawer');
  const body = dialog && dialog.querySelector('[data-qd-body]');
  if (!dialog || !body) { window.location.href = link.href; return; }
  if (quickAbort) quickAbort.abort();
  quickAbort = new AbortController();
  body.setAttribute('aria-busy', 'true');
  body.replaceChildren();
  openOverlay(dialog, link);
  try {
    const url = new URL(href || link.dataset.quickView || link.href, window.location.origin);
    url.searchParams.set('section_id', 'quick-drawer');
    const res = await fetch(url, { signal: quickAbort.signal, credentials: 'same-origin' });
    if (!res.ok) throw new Error('http_' + res.status);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-qd-content]');
    if (!content) throw new Error('no_content');
    body.replaceChildren(document.importNode(content, true));
    body.setAttribute('aria-busy', 'false');
    init(body);
    const title = body.querySelector('[data-qd-title]');
    if (title) dialog.setAttribute('aria-label', title.textContent.trim());
  } catch (e) {
    if (e && e.name === 'AbortError') return;
    // fail open: the real product page always works
    closeOverlay(dialog, { restore: false });
    window.location.href = link.href;
  }
}

/* --------------------------------------------------------------- global */

export function start({ isCartPage = false } = {}) {
  init(document);
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (step && !step.closest('[data-cf-body]')) { stepQuantity(step); return; }
    const opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && !(isCartPage && opener.dataset.overlayOpen === 'minicart')) {
        e.preventDefault();
        openOverlay(dialog, opener);
      }
      return;
    }
    const close = e.target.closest('[data-overlay-close]');
    if (close) {
      const dialog = close.closest('dialog');
      dialog.dispatchEvent(new CustomEvent('pp:overlay-cancel', { bubbles: true }));
      if (!dialog.hasAttribute('data-overlay-managed')) closeOverlay(dialog);
      return;
    }
    const quick = e.target.closest('[data-quick-view]');
    if (quick) {
      if (plainClick(e)) {
        e.preventDefault();
        openQuickView(quick);
      }
      return;
    }
    // D176: own product links inside a product description open the same drawer
    const desc = e.target.closest('[data-quick-links] a[href]');
    if (desc && plainClick(e)) {
      const href = quickLinkTarget(desc);
      if (href) {
        e.preventDefault();
        openQuickView(desc, href);
      }
    }
  });
  document.addEventListener('input', (e) => {
    const g = e.target.closest('[data-qty]');
    if (g) syncStepper(g);
  });
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => dispose(e.target));
}
